import groupBy from 'lodash/groupBy'

import type { RestDay, Shift, View } from 'modules/shift_management/lib/types'
import Leaves from 'modules/shift_management/store/internal/collections/Leaves'
import RestDays from 'modules/shift_management/store/internal/collections/RestDays'
import Shifts from 'modules/shift_management/store/internal/collections/Shifts'
import ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'

export type ShiftsRowState = {
  shiftsRows: ShiftsRow[]
  paginatedShiftsRows: ShiftsRow[]
}

export function initialShiftsRowState(): ShiftsRowState {
  return { shiftsRows: [], paginatedShiftsRows: [] }
}

export type ShiftsRowsDispatch = (arg0: ShiftsRowsAction) => void

export type ShiftsRowsAction =
  | {
      type: 'BOOTSTRAP'
      shiftsRows: ShiftsRow[]
    }
  | {
      type: 'ADD_SHIFTS_ROWS'
      shiftsRows: ShiftsRow[]
    }
  | {
      type: 'REFRESH_SHIFTS_ROWS'
      affectedEmployeeIds: number[]
      shiftsRows: ShiftsRow[]
    }
  | {
      type: 'ADD_SHIFTS'
      shifts: Shift[]
      view: View
    }
  | {
      type: 'ADD_REST_DAYS'
      restDays: RestDay[]
    }
  | {
      type: 'DESTROY_SHIFTS'
      shifts: Shift[]
      view: View
    }
  | {
      type: 'DESTROY_SHIFT'
      shift: Shift
      view: View
    }
  | {
      type: 'DESTROY_REST_DAYS'
      restDays: RestDay[]
    }
  | {
      type: 'DESTROY_REST_DAY'
      restDay: RestDay
    }
  | {
      type: 'RESET_SHIFTS_ROWS'
    }
  | {
      type: 'RESET_SHIFTS'
      shifts: Shift[]
      view: View
    }

export default function reducer(state: ShiftsRowState, action: ShiftsRowsAction): ShiftsRowState {
  switch (action.type) {
    case 'BOOTSTRAP': {
      return {
        ...state,
        shiftsRows: action.shiftsRows,
        paginatedShiftsRows: action.shiftsRows,
      }
    }

    case 'ADD_SHIFTS_ROWS': {
      if (action.shiftsRows.length === 0) return state

      return {
        ...state,
        shiftsRows: [...state.shiftsRows, ...action.shiftsRows],
        paginatedShiftsRows: [...action.shiftsRows],
      }
    }

    case 'REFRESH_SHIFTS_ROWS': {
      const affectedEmployeeIds: number[] = action.affectedEmployeeIds

      const updatedExistingShiftsRows = state.shiftsRows.map((shiftsRow) => {
        const newShiftsRow = action.shiftsRows.find((row) => row.key === shiftsRow.key)

        const newShifts = newShiftsRow ? newShiftsRow.shifts.shifts : []
        const previousShifts = shiftsRow.shifts.filterByNonEmployeeIds(affectedEmployeeIds)
        const newRestDays = newShiftsRow ? newShiftsRow.restDays.restDays : []
        const previousRestDays = shiftsRow.restDays.filterByNonEmployeeIds(affectedEmployeeIds)
        const previousLeaves = shiftsRow.leaves.filterByNonEmployeeIds(affectedEmployeeIds)
        const newLeaves = newShiftsRow ? newShiftsRow.leaves.leaves : []

        return new ShiftsRow({
          ...shiftsRow,
          type: shiftsRow._type,
          shifts: new Shifts([...previousShifts, ...newShifts]),
          restDays: new RestDays([...previousRestDays, ...newRestDays]),
          leaves: new Leaves([...previousLeaves, ...newLeaves]),
        })
      })

      const existingShiftsRowsKeys = new Set(state.shiftsRows.map((s: ShiftsRow) => s.key))
      const newShiftsRows = action.shiftsRows.filter(
        (s: ShiftsRow) => !existingShiftsRowsKeys.has(s.key)
      )

      return {
        ...state,
        shiftsRows: [...updatedExistingShiftsRows, ...newShiftsRows],
      }
    }

    case 'ADD_SHIFTS': {
      const viewShifts = action.view.isDaily
        ? action.shifts.filter((shift) => shift.isSameStartDay(action.view.firstDay))
        : action.shifts

      const groupedShifts = groupBy(viewShifts, (shift) => shift.rowKeyReference(action.view))
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, shifts] of Object.entries(groupedShifts)) {
        const index = newShiftsRows.findIndex((shiftsRow) => shiftsRow.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.addShifts(shifts)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'ADD_REST_DAYS': {
      const groupedRestDays = groupBy(action.restDays, (restDay) => restDay.employeeId.toString())
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, restDays] of Object.entries(groupedRestDays)) {
        const index = newShiftsRows.findIndex((x) => x.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.addRestDays(restDays)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'DESTROY_SHIFTS': {
      const groupedShifts = groupBy(action.shifts, (shift) => shift.rowKeyReference(action.view))
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, shifts] of Object.entries(groupedShifts)) {
        const index = newShiftsRows.findIndex((shiftsRow) => shiftsRow.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.destroyShifts(shifts)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'DESTROY_SHIFT': {
      const newShiftsRows = [...state.shiftsRows]
      const index = newShiftsRows.findIndex(
        (s) => s.key === action.shift.rowKeyReference(action.view)
      )

      if (index !== -1) {
        const shiftsRow = newShiftsRows[index]
        if (shiftsRow) {
          newShiftsRows[index] = shiftsRow.destroyShift(action.shift)
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'DESTROY_REST_DAY': {
      const newShiftsRows = [...state.shiftsRows]
      const index = newShiftsRows.findIndex((s) => s.key === action.restDay.employeeId.toString())

      if (index !== -1) {
        const shiftsRow = newShiftsRows[index]
        if (shiftsRow) {
          newShiftsRows[index] = shiftsRow.destroyRestDay(action.restDay)
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'DESTROY_REST_DAYS': {
      const groupedRestDays = groupBy(action.restDays, (restDay) => restDay.employeeId.toString())
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, restDays] of Object.entries(groupedRestDays)) {
        const index = newShiftsRows.findIndex((shiftsRow) => shiftsRow.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.destroyRestDays(restDays)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'RESET_SHIFTS_ROWS':
      return initialShiftsRowState()

    case 'RESET_SHIFTS': {
      const viewShifts = action.view.isDaily
        ? action.shifts.filter((shift) => shift.isSameStartDay(action.view.firstDay))
        : action.shifts

      const groupedShifts = groupBy(viewShifts, (shift) => shift.rowKeyReference(action.view))
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, shifts] of Object.entries(groupedShifts)) {
        const index = newShiftsRows.findIndex((shiftsRow) => shiftsRow.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.resetShifts(shifts)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    default:
      return state
  }
}
